<script setup lang="ts">
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'reka-ui';

import type { ComboboxProps } from '../types';

import { contentClasses, emptyClasses, itemClasses, viewportClasses } from '../classList';
import { comboboxDefaults } from '../types';
import { useComboboxSingle, useComboboxVersionedClasses } from '../useCombobox';

const props = withDefaults(defineProps<ComboboxProps>(), comboboxDefaults);

const emit = defineEmits<{
  blur: [];
  input: [value: string];
  clear: [];
}>();

const model = defineModel<string | number | null>({ required: true });

const { open, selected, getDisplayValue, clearSelection } = useComboboxSingle(props, model);
const { wrapperClasses, elementClasses, fontClasses } = useComboboxVersionedClasses(props, model, 'anchor');

const elementId = useId();
const hasError = computed(() => props.errorMessages.length > 0);
const errorsId = computed(() => `error-${elementId}`);

const hasValue = computed(() => !!model.value || (props.nullable && model.value === null));

// Emit blur when dropdown closes
watch(open, (newOpen, oldOpen) => {
  if (oldOpen === true && newOpen === false) {
    emit('blur');
  }
});

// Reka's built-in filter matches the typed term against each item's VALUE, which for
// option lists is an id (state ULIDs, enum keys), so it never matches what the user
// sees. Filter on labels ourselves and keep reka's filter off. Consumers that search
// server-side (ignoreFilter) get the options passed through untouched.
const searchTerm = ref('');

const filteredOptions = computed(() => {
  if (props.ignoreFilter || !searchTerm.value) {
    return props.options;
  }
  const term = searchTerm.value.toLowerCase();
  return props.options.filter(option => option.label.toLowerCase().includes(term));
});

// Typing opens the dropdown, so clearing on open would wipe the term mid-keystroke.
// Clearing on close means the next open shows the full list again.
watch(open, (isOpen) => {
  if (!isOpen) {
    searchTerm.value = '';
  }
});

const onInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchTerm.value = target.value;
  emit('input', target.value);
};

// Also emit blur when component loses focus (e.g., tabbing past)
const handleFocusOut = () => {
  emit('blur');
};

const onClear = () => {
  clearSelection();
  emit('clear');
};
</script>

<template>
  <div class="group/kui-input" @focusout="handleFocusOut">
    <ComboboxRoot
      v-model="selected"
      v-model:open="open"
      class="relative"
      :disabled="disabled"
      ignore-filter
      :data-testid="dataTestId"
    >
      <!-- v2 labels carry the same mb-2 as KUICoreInputText and the Select/Multiple siblings,
           so side-by-side fields keep their boxes on one line. -->
      <KUICoreInputLabel
        v-if="!hideLabel"
        :label="label || ''"
        :required="required"
        :class="[labelClass, { 'mb-2': version === 'v2' }]"
      />

      <ComboboxAnchor :class="wrapperClasses" @click.stop="open = true">
        <ComboboxInput
          :class="[elementClasses, fontClasses]"
          :data-testid="`${dataTestId}-search-input`"
          :placeholder="placeholder"
          :display-value="getDisplayValue"
          :aria-invalid="hasError ? 'true' : undefined"
          :aria-describedby="hasError ? errorsId : undefined"
          @input="onInputChange"
        />
        <div class="flex shrink-0 items-center">
          <ComboboxTrigger
            v-if="!clearable || (clearable && !hasValue)"
            class="data-disabled:cursor-not-allowed"
            :data-testid="`${dataTestId}-trigger`"
            @click.stop
          >
            <KUICoreButtonIcon :icon="ICONS.arrowDown" variant="tertiary" />
          </ComboboxTrigger>
          <Icon
            v-else
            :name="ICONS.cancel"
            size="lg"
            class="ms-auto"
            :data-testid="`${dataTestId}-clear-button`"
            @click="onClear"
          />
        </div>
      </ComboboxAnchor>
      <ComboboxPortal>
        <ComboboxContent
          :class="contentClasses"
          class="w-[var(--reka-combobox-trigger-width)]! z-101"
          position="popper"
        >
          <ComboboxViewport
            :class="viewportClasses"
            class="max-h-64"
            :data-testid="`${dataTestId}-listbox`"
          >
            <ComboboxEmpty :class="emptyClasses">
              <span v-if="loading">Loading...</span>
            </ComboboxEmpty>
            <ComboboxItem
              v-for="option in filteredOptions"
              :key="option.label"
              :value="option.value"
              :class="itemClasses"
            >
              {{ option.label }}
            </ComboboxItem>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
    <KUICoreInputErrors
      v-if="errorMessages.length"
      :id="errorsId"
      :error-messages="errorMessages"
    />
  </div>
</template>
